// @givmo/app-admin — api-client wiring (Combined Plan 03 §4 consumer).
//
// We CONSUME @givmo/api-client (the typed fetch client + the ported Dio interceptor stack: Bearer ID
// token, X-Firebase-AppCheck, X-Request-Id correlation, {data,meta,links} envelope unwrap,
// page[limit]/page[offset]); we do NOT re-implement it. This module only supplies the admin-app
// CONFIGURATION and adapts the donate app's SSR-per-request wiring to the SPA browser context:
//   - SSR donate built an ISOLATED per-request client with a server-minted App Check token.
//   - The SPA configures the SHARED singleton ONCE at boot; tokens come from the LIVE Firebase
//     session — the Bearer ID token from @givmo/auth's `auth.currentUser`, App Check from the
//     reCAPTCHA Enterprise provider — both resolved lazily per request.
//
// Admin-app config: `appPrefix: 'wb'` (the webapp/admin X-Request-Id prefix; donate uses 'dw'),
// base URL from env (api-dev.givmo.io dev / api.givmo.io prod — env-injected), Bearer token provider,
// App Check provider, and the console network logger gated on `!isProduction`.

import {
  configureGivmoClient,
  type ConfiguredGivmoClient,
  type FirebaseAuthProvider,
  type FirebaseAppCheckProvider,
} from '@givmo/api-client';
import { getGivmoAppCheckToken, type SuiteSessionTransport } from '@givmo/auth';
import type { Auth } from 'firebase/auth';
import type { AppCheck } from 'firebase/app-check';
import type { AdminEnv } from './env';
import { isProduction } from './env';
import { ADMIN_REQUEST_ID_PREFIX } from './constants';

/**
 * Bearer ID-token provider backed by the live Firebase Auth session. Returns `null` when no user is
 * signed in (the api-client middleware then omits the `Authorization` header — unauthenticated calls
 * such as the pre-login surface still work). `forceRefresh` bypasses the cached token (e.g. after a
 * 401) per the {@link FirebaseAuthProvider} contract.
 */
export function firebaseAuthProvider(auth: Auth): FirebaseAuthProvider {
  return {
    async getIdToken(forceRefresh = false): Promise<string | null> {
      const user = auth.currentUser;
      if (user === null) return null;
      return user.getIdToken(forceRefresh);
    },
  };
}

/**
 * App Check token provider backed by the reCAPTCHA Enterprise AppCheck instance. FAIL-CLOSED via
 * @givmo/auth's `getGivmoAppCheckToken` (it throws rather than minting an empty token). When App
 * Check is not configured (`appCheck` is `null` — local/dev), returns a provider that yields `null`
 * so the middleware omits the header (the backend bypasses App Check in local/dev).
 */
export function firebaseAppCheckProvider(appCheck: AppCheck | null): FirebaseAppCheckProvider {
  if (appCheck === null) {
    return { getToken: async () => null };
  }
  return {
    async getToken(): Promise<string | null> {
      const { token } = await getGivmoAppCheckToken(appCheck);
      return token;
    },
  };
}

/**
 * EP-AI1 — the auth headers for the assistant's SSE connection.
 *
 * ⚠ THE STREAM CANNOT GO THROUGH THE SHARED FETCH CLIENT, so it cannot inherit the interceptor stack.
 * `@givmo/ai`'s stream client reads a `ReadableStream` body incrementally and reconnects with its own
 * `Last-Event-ID`; the generated client parses a whole response and knows nothing about resuming. So
 * the two load-bearing headers are resolved here instead, from the SAME providers the interceptors
 * use, and minted per (re)connect rather than captured once — a stream can outlive a token.
 *
 * It is a module singleton set at boot for the same reason the api-client itself is: the panel mounts
 * deep inside the router, and threading Firebase handles down to it would put an auth dependency on
 * every route between here and there.
 */
let resolveStreamHeaders: (() => Promise<Record<string, string>>) | null = null;

/** Auth headers for the assistant stream. Empty before boot has configured the client, and empty for
 *  a signed-out session — the backend then refuses the stream, which is the correct outcome. */
export function assistantStreamHeaders(): Promise<Record<string, string>> {
  return resolveStreamHeaders?.() ?? Promise.resolve({});
}

export interface ConfigureAdminClientOptions {
  env: AdminEnv;
  auth: Auth;
  appCheck: AppCheck | null;
}

/**
 * Configure the shared @givmo/api-client singleton for the admin app. Call ONCE at boot; every
 * generated `sdk.*` call and TanStack Query option factory then inherits the base URL + interceptor
 * stack. Returns the configured client handle.
 */
export function configureAdminClient(options: ConfigureAdminClientOptions): ConfiguredGivmoClient {
  const { env, auth, appCheck } = options;
  const authProvider = firebaseAuthProvider(auth);
  const appCheckProvider = firebaseAppCheckProvider(appCheck);
  resolveStreamHeaders = async () => {
    const headers: Record<string, string> = {};
    const idToken = await authProvider.getIdToken();
    if (idToken !== null) headers.Authorization = `Bearer ${idToken}`;
    // Fail-SOFT here, unlike the request interceptor: App Check throws rather than minting an empty
    // token, and a stream that never opens because attestation hiccuped is worse for a person
    // mid-conversation than one the backend refuses with a reason.
    try {
      const appCheckToken = await appCheckProvider.getToken();
      if (appCheckToken !== null) headers['X-Firebase-AppCheck'] = appCheckToken;
    } catch {
      // Left off; the backend decides.
    }
    return headers;
  };
  return configureGivmoClient({
    appPrefix: ADMIN_REQUEST_ID_PREFIX,
    baseUrl: env.apiBaseUrl,
    auth: authProvider,
    appCheck: appCheckProvider,
    debugLogEnabled: !isProduction(env),
  });
}

/**
 * The transport `@givmo/auth`'s suite-session primitives need to reach the API host (AS-SUITE4).
 *
 * Deliberately built from the app's EXISTING App Check provider rather than a second token path, so
 * the fail-closed posture is the same one every other request here has: a configured App Check that
 * cannot mint THROWS, and the primitive abandons the call rather than sending it unattested; an
 * unconfigured one (local/dev, no site key) resolves `null` and the header is omitted, exactly as
 * the api-client middleware omits it.
 *
 * `baseUrl` is the same `env.apiBaseUrl` the client is configured with — the suite cookie is
 * host-only on the API host, so any other value silently yields "no suite session".
 */
export function suiteSessionTransport(
  env: AdminEnv,
  appCheck: AppCheck | null,
): SuiteSessionTransport {
  const provider = firebaseAppCheckProvider(appCheck);
  return { baseUrl: env.apiBaseUrl, appCheckToken: () => provider.getToken() };
}
